import {
  Docket,
  PrinterProfile,
  SortBy,
  GroupBy,
  Category,
  DocketItem,
  DEFAULT_TABLE_ABBREVIATION,
  DocketItemStatus,
} from '@oolio-group/domain';

function isPriorityStatus(item: DocketItem): boolean {
  return (
    item.itemStatus === DocketItemStatus.CREATED ||
    item.itemStatus === DocketItemStatus.RECALLED
  );
}

function comparePriorityItems(
  a: DocketItem,
  b: DocketItem,
  groupItemsByStatus: boolean,
): number {
  if (groupItemsByStatus) {
    const aIsPriority = isPriorityStatus(a);
    const bIsPriority = isPriorityStatus(b);

    if (aIsPriority && !bIsPriority) {
      return -1; // a is CREATED/RECALLED, b is not
    }
    if (!aIsPriority && bIsPriority) {
      return 1; // b is CREATED/RECALLED, a is not
    }
  } else {
    // When grouping is disabled, prioritize original order (createdAt)
    const createdAtTimeA = a?.createdAt ?? 0;
    const createdAtTimeB = b?.createdAt ?? 0;
    if (createdAtTimeA !== createdAtTimeB) {
      return createdAtTimeA - createdAtTimeB;
    }
  }
  return 0; // No priority difference, continue with other sorting criteria
}

function sortGroupItems(
  items: DocketItem[],
  sortBy: SortBy,
  groupItemsByStatus = true,
): void {
  // Implement sorting logic based on sortBy parameter
  switch (sortBy) {
    case SortBy.EXACT_CART_ORDER:
      items.sort((a, b) => {
        if (groupItemsByStatus) {
          // When grouping is enabled, prioritize CREATED/RECALLED items
          const aIsPriority = isPriorityStatus(a);
          const bIsPriority = isPriorityStatus(b);

          if (aIsPriority && !bIsPriority) {
            return -1; // a is CREATED/RECALLED, b is not
          }
          if (!aIsPriority && bIsPriority) {
            return 1; // b is CREATED/RECALLED, a is not
          }
        }
        // Always sort by creation time to maintain original order
        const createdAtTimeA = a?.createdAt ?? 0;
        const createdAtTimeB = b?.createdAt ?? 0;

        return createdAtTimeA - createdAtTimeB;
      });
      break;

    case SortBy.SEQUENCE_NUMBER:
      items.sort((a, b) => {
        // First priority: when grouping is enabled, prioritize CREATED/RECALLED items
        const priorityComparison = comparePriorityItems(
          a,
          b,
          groupItemsByStatus,
        );
        if (priorityComparison !== 0) {
          return priorityComparison;
        }

        // Continue with sequence number sorting criteria
        const pagePriorityA = a?.pagePriority ?? Number.MAX_SAFE_INTEGER;
        const pagePriorityB = b?.pagePriority ?? Number.MAX_SAFE_INTEGER;
        if (pagePriorityA !== pagePriorityB) {
          return pagePriorityA - pagePriorityB;
        }
        // Secondary sort by priority
        const priorityA = a?.priority ?? Number.MAX_SAFE_INTEGER;
        const priorityB = b?.priority ?? Number.MAX_SAFE_INTEGER;
        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }
        // Tertiary sort by seatNumber
        const seatA = a?.seatNumber ?? -1;
        const seatB = b?.seatNumber ?? -1;
        if (seatA !== seatB) {
          return seatA - seatB;
        }

        return 0;
      });
      break;

    case SortBy.SEAT:
      items.sort((a, b) => {
        // First priority: when grouping is enabled, prioritize CREATED/RECALLED items
        const priorityComparison = comparePriorityItems(
          a,
          b,
          groupItemsByStatus,
        );
        if (priorityComparison !== 0) {
          return priorityComparison;
        }

        // Continue with seat sorting criteria
        const seatNumberA = a?.seatNumber ?? -1;
        const seatNumberB = b?.seatNumber ?? -1;
        if (seatNumberA !== seatNumberB) {
          return seatNumberA - seatNumberB;
        }
        return 0;
      });
      break;

    default:
      // No sorting if the sortBy value isn't handled
      break;
  }
}

export function sortDocketItems(
  filteredDockets: Docket[],
  assignedProfiles: PrinterProfile[],
  categories?: Category[],
  groupItemsByStatus = true,
): Docket[] {
  if (!filteredDockets) return [];

  const categoryMap = new Map<string, string>();
  if (categories?.length) {
    categories.forEach(category => {
      if (category.id) {
        categoryMap.set(category.id, category.name || '');
      }
    });
  }

  return filteredDockets.map(docket => {
    const newDocket = { ...docket };
    const profile = assignedProfiles.find(
      p => p.id === docket.printerProfileId,
    );
    const isSortingEnabled = profile?.sorting?.enabled ?? false;
    const sortBy = profile?.sorting?.sortBy || SortBy.EXACT_CART_ORDER;
    const groupingType = profile?.grouping?.groupBy;
    const isGroupingEnabled = profile?.grouping?.enabled;

    if (!newDocket.docketItems) {
      newDocket.docketItems = [];
      return newDocket;
    }

    if (isGroupingEnabled) {
      // Handle hierarchical grouping based on primary grouping type
      if (groupingType === GroupBy.SEAT) {
        // Primary grouping by SEAT with sub-grouping by COURSE
        const seatGroups: Map<string, DocketItem[]> = new Map();

        newDocket.docketItems.forEach(docketItem => {
          const seatKey =
            docketItem?.seatNumber?.toString() || DEFAULT_TABLE_ABBREVIATION;
          if (!seatGroups.has(seatKey)) {
            seatGroups.set(seatKey, []);
          }
          seatGroups.get(seatKey)?.push(docketItem);
        });

        // Sort seat keys
        const seatKeys = Array.from(seatGroups.keys());
        seatKeys.sort((a, b) => {
          // Put DEFAULT_TABLE_ABBREVIATION at the start
          if (a === DEFAULT_TABLE_ABBREVIATION) return -1;
          if (b === DEFAULT_TABLE_ABBREVIATION) return 1;

          // Try to sort numerically
          const numA = Number.parseInt(a, 10);
          const numB = Number.parseInt(b, 10);
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
          }

          return a.localeCompare(b);
        });

        const result: DocketItem[] = [];

        // Process each seat group
        seatKeys.forEach(seatKey => {
          const itemsInSeat = seatGroups.get(seatKey) ?? [];

          // Create course sub-groups
          const courseGroups: Map<string, DocketItem[]> = new Map();
          const coursePriorities: Map<string, number> = new Map();

          itemsInSeat.forEach(item => {
            const courseKey = item.course?.id || 'now';
            if (!courseGroups.has(courseKey)) {
              courseGroups.set(courseKey, []);

              // Store course priority for sorting
              const coursePriority =
                courseKey === 'now'
                  ? -1
                  : item.course?.priority ?? Number.MAX_SAFE_INTEGER;

              coursePriorities.set(courseKey, coursePriority);
            }
            courseGroups.get(courseKey)?.push(item);
          });

          // Sort each course group if sorting is enabled
          if (isSortingEnabled) {
            courseGroups.forEach(groupItems => {
              sortGroupItems(groupItems, sortBy, groupItemsByStatus);
            });
          }

          // Sort course keys by priority
          const courseKeys = Array.from(courseGroups.keys());
          courseKeys.sort((a, b) => {
            const priorityA = coursePriorities.get(a) ?? -1;
            const priorityB = coursePriorities.get(b) ?? -1;
            return priorityA - priorityB;
          });

          // Add items from each course group in priority order
          courseKeys.forEach(courseKey => {
            result.push(...(courseGroups.get(courseKey) ?? []));
          });
        });

        newDocket.docketItems = result;
      } else if (groupingType === GroupBy.CATEGORY) {
        // Primary grouping by CATEGORY with sub-grouping by COURSE and third-level grouping by SEAT
        const categoryGroups: Map<string, DocketItem[]> = new Map();

        newDocket.docketItems.forEach(docketItem => {
          const categoryKey = docketItem.product?.category?.id || 'no-category';
          if (!categoryGroups.has(categoryKey)) {
            categoryGroups.set(categoryKey, []);
          }
          categoryGroups.get(categoryKey)?.push(docketItem);
        });

        // Sort category keys
        const categoryKeys = Array.from(categoryGroups.keys());

        if (categories) {
          categoryKeys.sort((a, b) => {
            if (a === 'no-category') return 1;
            if (b === 'no-category') return -1;

            const nameA = a ? categoryMap.get(a) || a : a;
            const nameB = b ? categoryMap.get(b) || b : b;

            return nameA.localeCompare(nameB);
          });
        }

        const result: DocketItem[] = [];

        // Process each category group
        categoryKeys.forEach(categoryKey => {
          const itemsInCategory = categoryGroups.get(categoryKey) ?? [];

          // Create course sub-groups
          const courseGroups: Map<string, DocketItem[]> = new Map();
          const coursePriorities: Map<string, number> = new Map();

          itemsInCategory.forEach(item => {
            const courseKey = item.course?.id || 'now';
            if (!courseGroups.has(courseKey)) {
              courseGroups.set(courseKey, []);

              // Store course priority for sorting
              const coursePriority =
                courseKey === 'now' ? -1 : item.course?.priority ?? -1;

              coursePriorities.set(courseKey, coursePriority);
            }
            courseGroups.get(courseKey)?.push(item);
          });

          // Sort course keys by priority
          const courseKeys = Array.from(courseGroups.keys());
          courseKeys.sort((a, b) => {
            const priorityA = coursePriorities.get(a) ?? -1;
            const priorityB = coursePriorities.get(b) ?? -1;
            return priorityA - priorityB;
          });

          // Process each course group
          courseKeys.forEach(courseKey => {
            const itemsInCourse = courseGroups.get(courseKey) ?? [];

            // Create seat sub-groups within this course
            const seatGroups: Map<string, DocketItem[]> = new Map();

            itemsInCourse.forEach(item => {
              const seatKey =
                item?.seatNumber?.toString() || DEFAULT_TABLE_ABBREVIATION;
              if (!seatGroups.has(seatKey)) {
                seatGroups.set(seatKey, []);
              }
              seatGroups.get(seatKey)?.push(item);
            });

            // Sort each seat group if sorting is enabled
            if (isSortingEnabled) {
              seatGroups.forEach(groupItems => {
                sortGroupItems(groupItems, sortBy, groupItemsByStatus);
              });
            }

            // Sort seat keys
            const seatKeys = Array.from(seatGroups.keys());
            seatKeys.sort((a, b) => {
              // Put DEFAULT_TABLE_ABBREVIATION at the start
              if (a === DEFAULT_TABLE_ABBREVIATION) return -1;
              if (b === DEFAULT_TABLE_ABBREVIATION) return 1;

              // Try to sort numerically
              const numA = Number.parseInt(a, 10);
              const numB = Number.parseInt(b, 10);
              if (!isNaN(numA) && !isNaN(numB)) {
                return numA - numB;
              }

              return a.localeCompare(b);
            });

            // Add items from each seat group in seat order
            seatKeys.forEach(seatKey => {
              result.push(...(seatGroups.get(seatKey) ?? []));
            });
          });
        });

        newDocket.docketItems = result;
      } else if (groupingType === GroupBy.COURSE) {
        // Primary grouping by COURSE with sub-grouping by SEAT
        const courseGroups: Map<string, DocketItem[]> = new Map();
        const coursePriorities: Map<string, number> = new Map();

        newDocket.docketItems.forEach(docketItem => {
          const courseKey = docketItem.course?.id || 'now';
          if (!courseGroups.has(courseKey)) {
            courseGroups.set(courseKey, []);

            // Store course priority for sorting
            const coursePriority =
              courseKey === 'now'
                ? -1
                : docketItem.course?.priority ?? Number.MAX_SAFE_INTEGER;
            coursePriorities.set(courseKey, coursePriority);
          }
          courseGroups.get(courseKey)?.push(docketItem);
        });

        // Sort course keys by priority
        const courseKeys = Array.from(courseGroups.keys());
        courseKeys.sort((a, b) => {
          const priorityA = coursePriorities.get(a) ?? -1;
          const priorityB = coursePriorities.get(b) ?? -1;
          return priorityA - priorityB;
        });

        const result: DocketItem[] = [];

        // Process each course group
        courseKeys.forEach(courseKey => {
          const itemsInCourse = courseGroups.get(courseKey) ?? [];

          // Create seat sub-groups
          const seatGroups: Map<string, DocketItem[]> = new Map();

          itemsInCourse.forEach(item => {
            const seatKey =
              item?.seatNumber?.toString() || DEFAULT_TABLE_ABBREVIATION;
            if (!seatGroups.has(seatKey)) {
              seatGroups.set(seatKey, []);
            }
            seatGroups.get(seatKey)?.push(item);
          });

          // Sort each seat group if sorting is enabled
          if (isSortingEnabled) {
            seatGroups.forEach(groupItems => {
              sortGroupItems(groupItems, sortBy, groupItemsByStatus);
            });
          }

          // Sort seat keys
          const seatKeys = Array.from(seatGroups.keys());
          seatKeys.sort((a, b) => {
            // Put DEFAULT_TABLE_ABBREVIATION at the start
            if (a === DEFAULT_TABLE_ABBREVIATION) return -1;
            if (b === DEFAULT_TABLE_ABBREVIATION) return 1;

            // Try to sort numerically
            const numA = Number.parseInt(a, 10);
            const numB = Number.parseInt(b, 10);
            if (!isNaN(numA) && !isNaN(numB)) {
              return numA - numB;
            }

            return a.localeCompare(b);
          });

          // Add items from each seat group in seat order
          seatKeys.forEach(seatKey => {
            result.push(...(seatGroups.get(seatKey) ?? []));
          });
        });

        newDocket.docketItems = result;
      }
    } else if (isSortingEnabled) {
      // When grouping is disabled but sorting is enabled
      sortGroupItems(newDocket.docketItems, sortBy, groupItemsByStatus);
    }

    return newDocket;
  });
}
